// Dépenses / situation des charges : registre des dépenses réellement engagées, par mois.
// Saisie ligne à ligne, import d'un fichier Excel (format DATE / LIBELLÉ / MONTANT), export Excel, total du mois.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  AddOutlined, AttachFileOutlined, ChevronLeft, ChevronRight, CloseOutlined, DeleteOutline, EditOutlined, FileDownloadOutlined,
  OpenInNewOutlined, SearchOutlined, UploadFileOutlined,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Kpi, Panel } from '../../components/ui';
import ImportDialog from '../planning/ImportDialog';
import { depensesAPI } from '../../services/api';
import { apiError, today } from '../../utils/format';
import { ecrireExcel } from '../../utils/excel';
import { analyserDepenses } from '../../utils/depensesExcel';
import { tokens } from '../../theme';

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const libMois = (m) => `${MOIS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
const moisDe = (d) => d.slice(0, 7);
const decale = (m, n) => { const d = new Date(`${m}-01T00:00:00Z`); d.setUTCMonth(d.getUTCMonth() + n); return d.toISOString().slice(0, 7); };
const F = (n) => (n === null || n === undefined ? '—' : `${Math.round(n).toLocaleString('fr-FR')} F`);
const frDate = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
const MODES = [['ESPECES', 'Espèces'], ['WAVE', 'Wave'], ['ORANGE_MONEY', 'Orange Money'], ['VIREMENT', 'Virement'], ['CHEQUE', 'Chèque'], ['MOBILE_MONEY', 'Mobile money'], ['AUTRE', 'Autre']];
const libMode = (m) => (MODES.find((x) => x[0] === m) || [null, ''])[1];
const CATEGORIES = ['Matière', 'Carburant', 'Salaires', 'Journaliers', 'Entretien / réparation', 'Transport / péage', 'Fournitures', 'Prestations', 'Frais divers'];

export function DepenseDialog({ open, depense, moisDefaut, demandeId = null, onClose, onDone }) {
  const [f, setF] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [fichiers, setFichiers] = useState([]);   // pièces déjà enregistrées (dépense existante)
  const [pieces, setPieces] = useState([]);       // fichiers choisis, à envoyer après enregistrement
  const input = useRef();
  const chargerFichiers = useCallback((id) => depensesAPI.fichiers(id).then((r) => setFichiers(r.data)).catch(() => setFichiers([])), []);
  useEffect(() => {
    if (!open) return;
    setError(''); setPieces([]); setFichiers([]);
    if (depense?.id) chargerFichiers(depense.id);
    setF(depense ? { ...depense } : { date_depense: (moisDefaut && `${moisDefaut}-01` > today()) ? `${moisDefaut}-01` : today(), libelle: '', montant: '', categorie: '', fournisseur: '', piece: '', mode_paiement: '', quantite: '', prix_unitaire: '', observation: '' });
  }, [open, depense, moisDefaut]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const auto = f.montant === '' || f.montant === null || f.montant === undefined;
  const montantCalc = auto && f.quantite && f.prix_unitaire ? Math.round(Number(String(f.quantite).replace(',', '.')) * Number(String(f.prix_unitaire).replace(',', '.'))) : null;
  const voirFichier = async (fid) => { try { const r = await depensesAPI.fichierBlob(fid); const url = URL.createObjectURL(r.data); window.open(url, '_blank', 'noopener'); setTimeout(() => URL.revokeObjectURL(url), 60000); } catch (e) { setError(apiError(e)); } };
  const supprimerUnFichier = async (fid) => { if (!window.confirm('Supprimer cette pièce jointe ?')) return; try { await depensesAPI.supprimerFichier(fid); chargerFichiers(depense.id); } catch (e) { setError(apiError(e)); } };
  const choisir = (e) => { const fs = Array.from(e.target.files || []); setPieces((prev) => [...prev, ...fs].slice(0, 5)); e.target.value = ''; };
  const save = async () => {
    setBusy(true); setError('');
    try {
      const payload = { ...f, demande_id: f.demande_id || demandeId || null };
      let id = depense?.id;
      if (depense) await depensesAPI.update(id, payload); else { const r = await depensesAPI.create(payload); id = r.data.id; }
      for (const file of pieces) { await depensesAPI.joindreFichier(id, file); } // eslint-disable-line no-await-in-loop
      onDone(depense ? 'Dépense modifiée' : 'Dépense enregistrée');
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };
  if (!open) return null;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{depense ? 'Modifier la dépense' : 'Nouvelle dépense'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField type="date" label="Date *" value={f.date_depense || ''} onChange={set('date_depense')} InputLabelProps={{ shrink: true }} />
          <TextField select label="Catégorie" value={f.categorie || ''} onChange={set('categorie')}>
            <MenuItem value="">—</MenuItem>
            {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField label="Libellé * (nature de la dépense)" value={f.libelle || ''} onChange={set('libelle')} sx={{ gridColumn: '1 / -1' }} inputProps={{ maxLength: 255 }} />
          <TextField label="Quantité" value={f.quantite ?? ''} onChange={set('quantite')} inputProps={{ inputMode: 'decimal' }} />
          <TextField label="Prix unitaire (F)" value={f.prix_unitaire ?? ''} onChange={set('prix_unitaire')} inputProps={{ inputMode: 'decimal' }} />
          <TextField label="Montant (F)" value={f.montant ?? ''} onChange={set('montant')} inputProps={{ inputMode: 'decimal' }}
            placeholder={montantCalc ? String(montantCalc) : ''} helperText={montantCalc ? `Calculé : ${F(montantCalc)} (modifiable)` : 'Sinon quantité × prix'} sx={{ gridColumn: { xs: '1', sm: 'auto' } }} />
          <TextField label="Fournisseur / bénéficiaire" value={f.fournisseur || ''} onChange={set('fournisseur')} />
          <TextField label="N° de pièce / reçu" value={f.piece || ''} onChange={set('piece')} />
          <TextField select label="Mode de paiement" value={f.mode_paiement || ''} onChange={set('mode_paiement')}>
            <MenuItem value="">—</MenuItem>
            {MODES.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
          </TextField>
          <TextField label="Observation" value={f.observation || ''} onChange={set('observation')} sx={{ gridColumn: '1 / -1' }} multiline minRows={2} inputProps={{ maxLength: 255 }} />
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>Reçu ou facture</Typography>
          {fichiers.map((x) => (
            <Box key={`f${x.id}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
              <AttachFileOutlined fontSize="small" color="action" />
              <Typography sx={{ flex: 1, fontSize: 13.5 }} noWrap>{x.nom} <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>({Math.round(x.taille / 1024)} Ko)</Typography></Typography>
              <Tooltip title="Ouvrir"><IconButton size="small" onClick={() => voirFichier(x.id)}><OpenInNewOutlined fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Supprimer"><IconButton size="small" onClick={() => supprimerUnFichier(x.id)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
            </Box>
          ))}
          {pieces.map((file, i) => (
            <Box key={`p${file.name}${file.size}${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
              <AttachFileOutlined fontSize="small" color="action" />
              <Typography sx={{ flex: 1, fontSize: 13.5 }} noWrap>{file.name} <Chip size="small" label="à enregistrer" sx={{ height: 18 }} /></Typography>
              <Tooltip title="Retirer"><IconButton size="small" onClick={() => setPieces((prev) => prev.filter((_, j) => j !== i))}><CloseOutlined fontSize="small" /></IconButton></Tooltip>
            </Box>
          ))}
          {fichiers.length + pieces.length < 5 && (
            <Button size="small" startIcon={<AttachFileOutlined />} onClick={() => input.current?.click()} sx={{ mt: 0.5 }}>Joindre le reçu / la facture</Button>
          )}
          <input ref={input} type="file" hidden accept="image/*,application/pdf" multiple onChange={choisir} aria-label="Joindre un fichier" />
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>Image (photo) ou PDF, 5 fichiers maximum.</Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" disabled={busy || !f.libelle || (!f.montant && !montantCalc) || !f.date_depense} onClick={save}>Enregistrer</Button>
      </DialogActions>
    </Dialog>
  );
}

const Depenses = () => {
  const [params] = useSearchParams();
  const [mois, setMois] = useState(moisDe(today()));
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [resume, setResume] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [dlg, setDlg] = useState(null); // { depense } ou {}
  const [importer, setImporter] = useState(false);

  const charger = useCallback(() => {
    depensesAPI.list({ mois, q: q || undefined }).then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
    depensesAPI.resume().then((r) => setResume(r.data)).catch(() => {});
  }, [mois, q]);
  useEffect(() => { const t = setTimeout(charger, 200); return () => clearTimeout(t); }, [charger]);

  const totauxMois = useMemo(() => Object.fromEntries((resume?.mois || []).map((m) => [m.mois, m.total])), [resume]);
  const supprimer = async (d) => {
    if (!window.confirm(`Supprimer la dépense « ${d.libelle} » (${F(d.montant)}) ?`)) return;
    try { await depensesAPI.supprimer(d.id); setInfo('Dépense supprimée'); charger(); } catch (e) { setError(apiError(e)); }
  };
  const exporter = async () => {
    const lignes = [
      [{ value: `Dépenses — ${libMois(mois)}`, fontWeight: 'bold' }],
      ['DATE', 'LIBELLE', 'MONTANT', 'CATEGORIE', 'FOURNISSEUR', 'N° PIECE', 'MODE', 'OBSERVATION'],
      ...data.depenses.map((d) => [d.date_depense ? new Date(`${d.date_depense}T00:00:00Z`) : null, d.libelle, Math.round(d.montant), d.categorie || '', d.fournisseur || '', d.piece || '', libMode(d.mode_paiement), d.observation || '']),
      [], ['', 'TOTAL', Math.round(data.total)],
    ];
    await ecrireExcel(lignes, `depenses-${mois}.xlsx`, { largeurs: [12, 42, 14, 18, 22, 14, 14, 30] });
  };

  return (
    <Layout title="Dépenses (situation des charges)" plain
      actions={<>
        <Button startIcon={<UploadFileOutlined />} onClick={() => setImporter(true)}>Importer Excel</Button>
        <Button startIcon={<FileDownloadOutlined />} disabled={!data?.depenses.length} onClick={exporter}>Excel</Button>
        <Button variant="contained" startIcon={<AddOutlined />} onClick={() => setDlg({})}>Nouvelle dépense</Button>
      </>}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => setMois(decale(mois, -1))}><ChevronLeft /></IconButton>
        <Typography sx={{ fontWeight: 700, fontSize: 18, minWidth: 150, textAlign: 'center' }}>{libMois(mois)}</Typography>
        <IconButton onClick={() => setMois(decale(mois, 1))} disabled={mois >= moisDe(today())}><ChevronRight /></IconButton>
        <TextField size="small" placeholder="Rechercher (libellé, fournisseur, pièce)" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 280, flex: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment> }} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
        <Kpi compact tint="navy" label={`Total — ${libMois(mois)}`} value={F(data?.total)} valueSize={22} sub={`${data?.nombre || 0} dépense(s)`} />
        <Kpi compact tint="gray" label="Mois précédent" value={F(totauxMois[decale(mois, -1)])} valueSize={22} sub={libMois(decale(mois, -1))} />
        <Kpi compact tint="amber" label="Cumul 12 derniers mois" value={F((resume?.mois || []).filter((m) => m.mois > decale(mois, -12) && m.mois <= mois).reduce((s, m) => s + m.total, 0))} valueSize={22} sub="glissant" />
      </Box>

      <Panel flush>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell><TableCell>Libellé</TableCell><TableCell>Catégorie</TableCell>
                <TableCell>Fournisseur</TableCell><TableCell>N° pièce</TableCell><TableCell>Mode</TableCell>
                <TableCell align="right">Montant</TableCell><TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.depenses.map((d) => (
                <TableRow key={d.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{frDate(d.date_depense)}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 14 }}>{d.libelle}</Typography>
                    {d.demande_numero && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Demande {d.demande_numero}</Typography>}
                    {d.quantite ? <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{d.quantite} {d.unite || ''} × {F(d.prix_unitaire)}</Typography> : null}
                    {d.nb_fichiers > 0 && <Chip size="small" icon={<AttachFileOutlined />} label={`${d.nb_fichiers} pièce(s)`} variant="outlined" sx={{ height: 20, mt: 0.25 }} />}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{d.categorie || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{d.fournisseur || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{d.piece || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{libMode(d.mode_paiement) || '—'}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 600, whiteSpace: 'nowrap' }}>{F(d.montant)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Modifier"><IconButton size="small" onClick={() => setDlg({ depense: d })}><EditOutlined fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Supprimer"><IconButton size="small" onClick={() => supprimer(d)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {data && !data.depenses.length && <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>Aucune dépense pour {libMois(mois)}.{(resume?.mois || []).some((m) => m.total) ? ' Des dépenses existent sur d’autres mois (utilisez les flèches ci-dessus).' : ' Saisissez-en une ou importez le fichier Excel.'}</TableCell></TableRow>}
              {data && data.depenses.length > 0 && (
                <TableRow sx={{ '& td': { borderTop: '2px solid', borderColor: 'divider', fontWeight: 700 } }}>
                  <TableCell colSpan={6}>TOTAL — {libMois(mois)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{F(data.total)}</TableCell><TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      <DepenseDialog open={!!dlg} depense={dlg?.depense} moisDefaut={mois} onClose={() => setDlg(null)} onDone={(m) => { setDlg(null); setInfo(m); charger(); }} />
      <ImportDialog open={importer} titre="Importer des dépenses (Excel)" maxWidth="lg"
        consignes={'Le fichier doit contenir, pour chaque mois, les colonnes DATE, LIBELLÉ et MONTANT (comme votre « situation des charges »). Les colonnes CATÉGORIE, FOURNISSEUR, N° PIÈCE et MODE sont reprises si elles existent. Chaque onglet (un par mois) peut être sélectionné. La ligne TOTAL est ignorée.'}
        analyser={analyserDepenses} importer={async (payload) => (await depensesAPI.importer(payload)).data.message}
        onClose={() => setImporter(false)} onDone={async (m) => { setImporter(false); setInfo(m); setQ('');
          try { const r = await depensesAPI.resume(); setResume(r.data); if (r.data.mois?.[0]) setMois(r.data.mois[0].mois); } catch { charger(); } }} />
    </Layout>
  );
};

export default Depenses;
